import { Injectable } from '@angular/core';
import { EAiChatRoleType } from '@type/enums/aiEnum';
import { SPEECH_TOKEN } from '../constants/storageKey';
import { IAiAgentReq, IAiAgentV2Req } from '../types/request/reqAI';

export function getUrl(url: string) {
  return `/api/home/openapi/${url}`
}

// Token过期时间
const EXPIRATION_TIME = 1000 * 60 * 60 * 24 * 3

@Injectable({
  providedIn: 'root'
})
export class AiApiService {

  // --------------------------- Baidu API ----------------------------------

  // 从API获取语音合成 Access_Token
  private async _getSpeechSyncAccessTokenByAPI() {
    const res = await fetch(getUrl('voice_token'), { method: 'post' })
    return res.json()
  }

  // 返回语音合成token
  public async getSpeechSynAccessToken() {
    const nowTime = Date.now()
    const oldToken = localStorage.getItem(SPEECH_TOKEN)

    // token有值且没过期则使用现有的
    if (oldToken && JSON.parse(oldToken).pastTime > nowTime) {
      return JSON.parse(oldToken).token
    }

    const res = await this._getSpeechSyncAccessTokenByAPI()
    const token = res.data.access_token
    localStorage.setItem(SPEECH_TOKEN, JSON.stringify({
      token,
      pastTime: nowTime + EXPIRATION_TIME
    }))
    return token
  }

  // 小景AI智能体回复（集成）（弃用）
  public async getAiAgentReply(params: IAiAgentReq, signal: AbortSignal) {
    const res = await fetch((getUrl('acsp/xiaojing_ai')), {
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      signal
    })

    return res.body
  }

  // 小景AI智能体回复 V2 （集成）
  public async getAiAgentReplyV2(params: IAiAgentV2Req, signal: AbortSignal) {
    const res = await fetch((getUrl('acsp/xiaojing_ai_v2')), {
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      signal
    })

    return res.body
  }

  // 返回语音合成mp3（tex:合成的文本，tok：access_token）
  public async getSpeechSynthesis(tex: string, tok: string) {
    // const res = await fetch((`${environment.apiBaseUrl}baidutsn/text2audio`), {
    const res = await fetch((`/baidutsn/text2audio`), {
      method: 'post',
      body: `tex=${tex}&lan=zh&cuid=qjyl&ctp=1&tok=${tok}&per=5118&spd=6`
    })
    return res.blob()
  }

  // 返回语音识别内容
  public async getSpeechRecognition(speech: string, len: number, token: string) {
    const data = {
      "format": "pcm",
      "rate": 16000,
      "channel": 1,
      "cuid": 'qjylAI',
      token,
      len,
      speech,
    }

    // const res = await fetch((`${environment.apiBaseUrl}baiduvoc/server_api`), {
    const res = await fetch((`/baiduvoc/server_api`), {
      headers: { 'Content-Type': 'application/json' },
      method: 'post',
      body: JSON.stringify(data)
    })
    return res.json()
  }

  // ----------------------------- 内部 API --------------------------------

  // 根据指定的key返回对话上下文
  public async getMesRecordsByKeyPage(key: string, page_number: number, page_size: number) {
    const res = await fetch(getUrl('ai_context/list_all'), {
      method: 'post',
      body: JSON.stringify({
        key,
        page_number,
        page_size
      })
    })
    return res.json()
  }

  // 指定key传入一条聊天内容（弃用）
  public async insertMesRecord<IResponseData>(key: string, role: EAiChatRoleType, content: string, image_url: string, commodity: string): Promise<IResponseData> {
    const res = await fetch(getUrl('ai_context/create'), {
      method: 'post',
      body: JSON.stringify({
        key,
        role,
        content,
        image_url,
        data: commodity
      })
    })
    return res.json()
  }

  // 指定key传入一条聊天内容
  public async insertMesRecordByGroup<IGroupResponseData>(params: { key: string, role: EAiChatRoleType, content: string, image_url: string, data: string }[]): Promise<IGroupResponseData> {
    const res = await fetch(getUrl('ai_context/creates'), {
      method: 'post',
      body: JSON.stringify(params)
    })
    return res.json()
  }

  // 根据指定消息id修改消息内容
  public async updateMesRecord(id: number, content: string, commodity?: string) {
    const res = await fetch(getUrl('ai_context/update'), {
      method: 'post',
      body: JSON.stringify({
        id,
        content,
        data: commodity
      })
    })
    return res.body
  }

  // 上传图片获取地址内容
  public async uploadPic<IUploadPic>(data: FormData): Promise<IUploadPic> {
    const res = await fetch(getUrl('ai_context/upload'), {
      method: 'post',
      body: data
    })
    return res.json()
  }

}
